import React, { useEffect, useState } from "react";
import { bufToNum, numToBuf, applyDigitKey, digitsOnly } from "./numericBuffer";

// Digit-only numeric input for the register. Behaves like a POS amount pad: only digits are
// accepted (letters, ".", "-", etc. are blocked), and they fill in from the right so the field
// always shows a valid, fixed-decimals number (e.g. money → "0.00"; type 1,2,5 → "1.25"). Controlled
// via a number `value` + `onChange(number)`. `decimals=2` for money/percent, `0` for quantities.
// `max` caps the value. On touch it opens the OS numeric keyboard (inputMode="numeric").
export default function NumericInput({ value, onChange, decimals = 2, max, style, autoFocus, placeholder, onEnter, ...rest }) {
  const [buf, setBuf] = useState(() => numToBuf(value, decimals));

  // Resync from the parent only on a genuine external change (not our own onChange echo).
  useEffect(() => {
    if (Math.abs(bufToNum(buf, decimals) - (Number(value) || 0)) > 1e-9) setBuf(numToBuf(value, decimals));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, decimals]);

  const push = (key) => {
    const r = applyDigitKey(buf, key, { decimals, max });
    setBuf(r.buf);
    if (onChange) onChange(r.value);
  };

  const onKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey) return; // allow copy/paste/select-all shortcuts
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      push(e.key);
    } else if (e.key === "Backspace") {
      e.preventDefault();
      push("DEL");
    } else if (e.key === "Delete") {
      e.preventDefault();
      push("C");
    } else if (e.key === "Enter") {
      if (onEnter) {
        e.preventDefault();
        onEnter();
      }
    } else if (["Tab", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
      return; // navigation keys pass through
    } else {
      e.preventDefault(); // block everything else (letters, ".", "-", "e", …)
    }
  };

  const onPaste = (e) => {
    e.preventDefault();
    let b = buf;
    for (const ch of digitsOnly(e.clipboardData.getData("text"))) {
      b = applyDigitKey(b, ch, { decimals, max }).buf;
    }
    setBuf(b);
    if (onChange) onChange(bufToNum(b, decimals));
  };

  const display = bufToNum(buf, decimals).toFixed(decimals);

  return (
    <input
      {...rest}
      type="text"
      inputMode="numeric"
      value={display}
      onChange={() => {}}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      onFocus={(e) => e.target.select()}
      style={style}
      autoFocus={autoFocus}
      placeholder={placeholder}
    />
  );
}
